import React, { useState, useEffect, useCallback } from "react";
import { Box, Typography, CircularProgress } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import FilterTabs from "./FilterTabs";
import SectionGroup from "./SectionGroup";
import RequestCard from "./RequestCard";
import AllocationWizard from "../MyAllocations/AllocationWizard";
import {
  getStatusFlowSummary,
  getStatusFlow,
} from "../../../../../api/statusFlow";
import PageInfo from "../../../../../components/common/PageInfo";

const groupByStatus = (requests) => {
  const groups = { AT_PMO: [], AT_SCG: [], ACTIVE: [], REJECTED: [] };
  requests.forEach((r) => {
    if (groups[r.status]) groups[r.status].push(r);
  });
  return groups;
};

const SECTION_ORDER = ["AT_PMO", "AT_SCG", "ACTIVE", "REJECTED"];

// Map the flat API response shape → nested shape AllocationWizard expects
const toWizardRow = (r) => ({
  id: r.id,
  requestCode: r.requestCode,
  status: r.status,
  fte: r.fte,
  startDate: r.startDate ?? "",
  endDate: r.endDate ?? "",
  notes: r.notes ?? "",
  rejectionReason: r.rejectionReason || null,
  accountName: r.accountName ?? r.position?.accountName ?? null,

  position: {
    positionId: r.position.positionId,
    positionCode: r.position.positionCode,
    roleTitle: r.position.roleTitle,
    sowName: r.position.sowName,
    positionType: r.position.positionType ?? "BIL",
    levelCode: r.position.levelCode,
    createdAt: r.position.createdAt ?? null, // ← ADD
    endDate: r.position.endDate ?? null, // ← ADD
    accountName: r.position?.accountName ?? r.accountName ?? null,
  },

  associate: {
    associateId: r.associate.associateId || null,
    fullName: r.associate.fullName,
    levelCode: r.associate.levelCode,
    initials: r.associate.initials,
  },

  skills: r.skills ?? [],

  parentRequestId: r.parentRequestId || null,
  parentRequestCode: r.parentRequestCode || null,
  parentRequestStatus: r.parentRequestStatus || null,
});

const StatusFlow = () => {
  const theme = useTheme();
  const t = theme.custom;

  const [summary, setSummary] = useState(null);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeFilter, setActiveFilter] = useState("all");
  const [search, setSearch] = useState("");

  // Wizard state
  const [wizardOpen, setWizardOpen] = useState(false);
  const [editRow, setEditRow] = useState(null);

  useEffect(() => {
    getStatusFlowSummary()
      .then(setSummary)
      .catch(() => {});
  }, []);

  const FILTER_TO_STATUS = {
    "all":      undefined,
    "at-pmo":   "SUBMITTED,MOVE_PENDING",
    "at-scg":   "PMO_APPROVED",
    "rejected": "REJECTED",
  };

  const fetchRequests = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const statusParam = FILTER_TO_STATUS[activeFilter];
      const data = await getStatusFlow({
        filter: activeFilter,
        status: statusParam,
        search: search || undefined,
      });
      setRequests(data);
    } catch (err) {
      console.error("Failed to load status flow:", err);
      setError("Failed to load requests");
    } finally {
      setLoading(false);
    }
  }, [activeFilter, search]);

  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);

  // Map flat API row → wizard shape before opening
  const handleResubmit = (row) => {
    setEditRow(toWizardRow(row));
    setWizardOpen(true);
  };

  const handleWizardClose = () => {
    setWizardOpen(false);
    setEditRow(null);
  };

  const handleWizardSubmitted = () => {
    setWizardOpen(false);
    setEditRow(null);
    fetchRequests();
  };

  const grouped = groupByStatus(requests);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        flex: 1,
        minHeight: 0,
        overflow: "hidden",
      }}
    >
      {/* ══ STATIC HEADER ZONE ══════════════════════════════════════════════ */}
      <Box sx={{ flexShrink: 0, padding: "20px 24px 0" }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: "20px",
          }}
        >
          <Typography
            sx={{
              fontFamily: "'Inter', sans-serif",
              fontSize: "20px",
              fontWeight: 700,
              color: t.text,
              letterSpacing: "-0.01em",
            }}
          >
            Status Flow
          </Typography>

          
        </Box>

        <PageInfo message="Check the status of your allocation requests and approval queue"/>

        <FilterTabs
          activeFilter={activeFilter}
          onFilterChange={(f) => {
            setActiveFilter(f);
            setSearch("");
          }}
          summary={summary}
          search={search}
          onSearchChange={setSearch}
        />
      </Box>

      {/* ══ SCROLLABLE CARDS ZONE ═══════════════════════════════════════════ */}
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          padding: "0 24px 24px",
          "&::-webkit-scrollbar": { width: "4px" },
          "&::-webkit-scrollbar-track": { background: "transparent" },
          "&::-webkit-scrollbar-thumb": {
            background: t.border3,
            borderRadius: "2px",
          },
        }}
      >
        {loading && (
          <Box sx={{ display: "flex", justifyContent: "center", pt: "40px" }}>
            <CircularProgress size={20} sx={{ color: t.accent }} />
          </Box>
        )}

        {!loading && error && (
          <Typography
            sx={{
              fontFamily: "'DM Mono', monospace",
              fontSize: "11px",
              color: t.error || "#c84040",
              padding: "12px 0",
            }}
          >
            {error}
          </Typography>
        )}

        {!loading && !error && requests.length === 0 && (
          <Box sx={{ display: "flex", justifyContent: "center", pt: "60px" }}>
            <Typography
              sx={{
                fontFamily: "'DM Mono', monospace",
                fontSize: "12px",
                color: t.text3,
                letterSpacing: "0.08em",
              }}
            >
              No requests found
            </Typography>
          </Box>
        )}

        {!loading &&
          !error &&
          requests.length > 0 &&
          activeFilter === "all" &&
          SECTION_ORDER.map(
            (status) =>
              grouped[status]?.length > 0 && (
                <SectionGroup
                  key={status}
                  status={status}
                  requests={grouped[status]}
                  onResubmit={handleResubmit}
                />
              ),
          )}

        {!loading &&
          !error &&
          requests.length > 0 &&
          activeFilter !== "all" &&
          requests.map((r) => (
            <RequestCard
              key={r.requestCode}
              request={r}
              onResubmit={handleResubmit}
            />
          ))}
      </Box>

      {/* Allocation wizard — edit/resubmit mode */}
      <AllocationWizard
        open={wizardOpen}
        onClose={handleWizardClose}
        onSubmitted={handleWizardSubmitted}
        editRow={editRow}
      />
    </Box>
  );
};

export default StatusFlow;
